import { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Text } from '@components/Text';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { getSelectedSiteDataSelector } from 'src/redesign/features/SelectedSite/store/selectors';
import { selectSettings } from 'src/features/settings-slice';
import { updateSiteData } from 'src/redesign/features/SelectedSite/store/actions';
import { showNotification } from 'src/redesign/features/Toast/store/actions';
import { Dialog } from 'primereact/dialog';
import { Pricing } from 'src/modules/sites/pricing';
import S from './TrialEndedBanner.module.scss';

export const TrialEndedBanner: FC = () => {
  const selectedSite = useAppSelector(getSelectedSiteDataSelector);
  const settings = useAppSelector(selectSettings);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [showPricingDialog, setShowPricingDialog] = useState(false);

  // Don't show if no selectedSite or settings
  if (!selectedSite || !settings) return null;

  // Only show for trial users
  if (settings.plan !== 'trial') return null;

  // Check if trial has ended (trial_days_left is 0 or less)
  const trialDaysEnded = selectedSite.trial_days_left !== undefined && selectedSite.trial_days_left <= 0;

  // Check if attendee limit has been reached (100%+)
  const orderCount = selectedSite.trial_order_count || 0;
  const orderLimit = selectedSite.trial_order_limit || 10;
  const attendeeLimitReached = orderCount >= orderLimit;

  // Only show if trial ended OR attendee limit reached
  if (!trialDaysEnded && !attendeeLimitReached) return null;

  // Don't show if user has already selected free plan
  if (selectedSite.trial_ended_free_plan_selected === true) return null;

  const handleFreePlanClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent banner click
    if (!selectedSite?.id) return;

    try {
      setLoading(true);
      await dispatch(
        updateSiteData({
          siteId: selectedSite.id,
          siteData: {
            trial_ended_free_plan_selected: true,
          },
        }),
      ).unwrap();

      dispatch(
        showNotification({
          detail: 'Site moved to Free plan. You can now create free events with up to 20 attendees per month.',
          severity: 'success',
          life: 5000,
        }),
      );
    } catch (error) {
      dispatch(
        showNotification({
          detail: 'Failed to update site. Please try again.',
          severity: 'error',
          life: 5000,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent banner click
    setShowPricingDialog(true);
  };

  const handleBannerClick = () => {
    setShowPricingDialog(true);
  };

  const bannerMainMessage = trialDaysEnded
    ? 'Trial ended — new orders are paused'
    : `Trial limit reached — new orders are paused (${orderCount}/${orderLimit} attendees)`;

  const hasStartedSelling = orderCount > 0;

  return (
    <>
      <div className={S.banner} onClick={handleBannerClick}>
        <div className={S.content}>
          <div className={S.messageSection}>
            <Text className={S.mainText} bold>
              ⚠️ {bannerMainMessage}
            </Text>
            <Text className={S.subText}>
              {hasStartedSelling
                ? "You've already started selling tickets — don't lose momentum. Upgrade to keep selling."
                : "Upgrade to keep selling tickets"}
            </Text>
          </div>
          <div className={S.actions}>
            <button
              onClick={handleUpgradeClick}
              disabled={loading}
              className={S.upgradeButton}
            >
              Resume Ticket Sales
            </button>
            <button
              onClick={handleFreePlanClick}
              disabled={loading}
              className={S.freeButton}
            >
              Continue with Free (no paid tickets)
            </button>
          </div>
        </div>
      </div>

      <Dialog
        visible={showPricingDialog}
        onHide={() => setShowPricingDialog(false)}
        header="Upgrade Your Plan"
        draggable={false}
        style={{ width: '80%' }}
        closable
        baseZIndex={100000}
      >
        <Pricing />
      </Dialog>
    </>
  );
};
